import React, { useState } from 'react';
import './HelpCenter.css';

const categories = [
  {
    title: 'Getting Started',
    desc: 'Set up your account and send your first application.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
  },
  {
    title: 'Account & Billing',
    desc: 'Manage your subscription, payments, and profile.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </svg>
    ),
  },
  {
    title: 'Applications',
    desc: 'Track, manage, and optimize your job applications.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
  },
  {
    title: 'Cold Emails',
    desc: 'Learn how AI-powered outreach works.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
  },
  {
    title: 'Resume & Cover Letters',
    desc: 'Upload, tailor, and manage your documents.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      </svg>
    ),
  },
  {
    title: 'Troubleshooting',
    desc: 'Fix common issues and get back on track.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
  },
];

const faqData = [
  {
    question: 'How do I connect my email account?',
    answer:
      'Go to Settings > Email Connections and click "Connect Gmail." You\'ll be guided through a secure OAuth 2.0 flow. We never see or store your password, and you can revoke access at any time.',
  },
  {
    question: 'How many applications can I send per day?',
    answer:
      'It depends on your plan. The Free plan allows up to 5 applications per day, the Pro plan allows up to 25, and the Premium plan supports up to 50 applications per day.',
  },
  {
    question: "Can I edit AI-generated cover letters before they're sent?",
    answer:
      'Absolutely. Every cover letter is presented for your review before submission. You can edit the text, adjust the tone, or rewrite it entirely. Nothing is sent without your approval.',
  },
  {
    question: 'How do I upgrade or downgrade my plan?',
    answer:
      'Head to Settings > Billing and click "Change Plan." Upgrades take effect immediately, and downgrades apply at the end of your current billing cycle. No cancellation fees.',
  },
  {
    question: 'What happens to my data if I delete my account?',
    answer:
      'When you delete your account, all personal data, including resumes, application history, and email connections, is permanently removed from our servers within 30 days, in compliance with GDPR and CCPA.',
  },
  {
    question: 'How do cold emails work?',
    answer:
      'ApplyAI identifies relevant recruiters and hiring managers, then drafts personalized outreach emails sent from your connected email. You review and approve every email before it goes out, and can track open and reply rates from your dashboard.',
  },
];

const HelpCenter = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div className="helpcenter-page">
      <div className="helpcenter-hero">
        <div className="helpcenter-hero-inner">
          <div className="helpcenter-header">
            <h1>Help Center</h1>
          </div>
          <p className="helpcenter-subtitle">
            Find answers to common questions about ApplyAI.
          </p>

          {/* Search bar (decorative) */}
          <div className="helpcenter-search">
            <span className="helpcenter-search-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              type="text"
              className="helpcenter-search-input"
            placeholder="Search for help..."
            readOnly
            />
          </div>
        </div>
      </div>

      <div className="helpcenter-layout">
        {/* Category cards */}
        <div className="helpcenter-categories">
          {categories.map((cat, index) => (
            <div key={index} className="helpcenter-category-card">
              <div className="helpcenter-category-icon">{cat.icon}</div>
              <p className="helpcenter-category-title">{cat.title}</p>
              <p className="helpcenter-category-desc">{cat.desc}</p>
            </div>
          ))}
        </div>

        {/* Popular Questions */}
        <div className="helpcenter-popular">
          <h2>Popular Questions</h2>
          <div className="helpcenter-accordion">
            {faqData.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={index}
                  className={`helpcenter-faq-item${isOpen ? ' helpcenter-faq-item--expanded' : ''}`}
                >
                  <button
                    className="helpcenter-faq-question"
                    onClick={() => handleToggle(index)}
                    aria-expanded={isOpen}
                  >
                    <span>{item.question}</span>
                    <span className="helpcenter-faq-toggle">+</span>
                  </button>
                  <div
                    className={`helpcenter-faq-answer-wrapper${isOpen ? ' helpcenter-faq-answer-wrapper--open' : ''}`}
                  >
                    <p className="helpcenter-faq-answer">{item.answer}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Still need help */}
        <div className="helpcenter-contact">
          <p className="helpcenter-contact-text">Still need help?</p>
          <a href="mailto:support@applyai.co" className="helpcenter-contact-email">
            Contact our support team at support@applyai.co
          </a>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
